import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import showdown from 'showdown';
import SafeHtmlBlock from '@/components/common/SafeHtmlBlock';
import { Loader } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { useT } from '@/i18n';
function hasHtml(input) {
    return /<\s*\w+[^>]*>/.test(input);
}
// Server ensures safety; keep HTML as-is so scripts can run
function sanitizeHtml(html) { return String(html || ''); }
const HtmlSection = ({ safeHtml, isStreaming, labels }) => {
    const [collapsed, setCollapsed] = React.useState(true);
    const toggle = () => { if (!isStreaming)
        setCollapsed((c) => !c); };
    return (_jsxs("div", { style: { display: 'block' }, children: [_jsxs("div", { onClick: toggle, style: {
                    display: 'flex', alignItems: 'center', gap: 8,
                    cursor: isStreaming ? 'default' : 'pointer',
                    padding: '8px 10px', marginTop: 4,
                    borderRadius: 8,
                    background: 'color-mix(in oklab, var(--app-bg-secondary) 85%, transparent)'
                }, children: [_jsx(IconChevronDown, { size: 14, style: { transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 120ms ease' } }), _jsx("span", { style: { fontSize: 12, opacity: 0.9 }, children: isStreaming ? labels.loading : (collapsed ? labels.expand : labels.collapse) }), isStreaming && _jsx(Loader, { size: "xs", style: { marginLeft: 6 } })] }), !collapsed && (_jsx("div", { style: { marginTop: 4 }, children: _jsx(SafeHtmlBlock, { html: safeHtml }) }))] }));
};
// Render processed HTML into a container and execute any inline scripts
const ScopedHtml = ({ html, className, style, onClick }) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        try {
            // Namespace duplicate IDs to avoid collisions across multiple messages
            let payload = String(html || '');
            const uid = 'm' + Math.random().toString(36).slice(2, 8);
            let ids = ['post-it-card', 'collapsible-content', 'options-container', 'raw-options-data'];
            // Also namespace common 'es-' ids dynamically if present
            try {
                const dyn = new Set();
                payload.replace(/id\s*=\s*([\"'])\s*([^'"\s>]+)\s*\1/gi, function (_m, _q, id) { const s = String(id); if (/^(es-|post-it-)/i.test(s))
                    dyn.add(s); return _m; });
                const adds = Array.from(dyn);
                ids = Array.from(new Set(ids.concat(adds)));
            }
            catch { }
            const map = {};
            ids.forEach((id) => { map[id] = `${uid}-${id}`; });
            try {
                // Replace id attributes for known ids
                ids.forEach((id) => {
                    payload = payload.replace(new RegExp(`(id=)(["'])${id}\\2`, 'g'), (_m, p1, q) => `${p1}${q}${map[id]}${q}`);
                });
                // Rewrite getElementById('id') usages
                ids.forEach((id) => {
                    payload = payload.replace(new RegExp(`getElementById\\(\\s*(["'])${id}\\1\\s*\\)`, 'g'), (_m, q) => `getElementById(${q}${map[id]}${q})`);
                });
                // Rewrite querySelector('#id') for our known ids (do NOT touch external selectors like #send_textarea)
                ids.forEach((id) => {
                    payload = payload.replace(new RegExp(`querySelector\\(\\s*(["'])#${id}\\1\\s*\\)`, 'g'), (_m, q) => `querySelector(${q}#${map[id]}${q})`);
                    payload = payload.replace(new RegExp(`querySelectorAll\\(\\s*(["'])#${id}\\1\\s*\\)`, 'g'), (_m, q) => `querySelectorAll(${q}#${map[id]}${q})`);
                });
            }
            catch { }
            el.innerHTML = payload;
            // Execute scripts by replacing them with new nodes
            const scripts = Array.from(el.querySelectorAll('script'));
            for (const s of scripts) {
                const ns = document.createElement('script');
                // copy attributes
                for (const attr of Array.from(s.attributes))
                    ns.setAttribute(attr.name, attr.value);
                ns.text = s.textContent || '';
                s.replaceWith(ns);
            }
            // If page-level ready events were registered after load, dispatch DOMContentLoaded once
            // Do NOT dispatch window 'load' (may trigger unrelated onload handlers with assumptions)
            try {
                if (document.readyState !== 'loading') {
                    setTimeout(() => { try {
                        document.dispatchEvent(new Event('DOMContentLoaded'));
                    }
                    catch { } }, 0);
                }
            }
            catch { }
        }
        catch { }
    }, [html]);
    return _jsx("div", { ref: ref, className: className, style: style, onClick: onClick });
};
// Beautified collapse for <think>/<thinking> blocks
const ThinkCollapse = ({ title = '创作思考', html }) => {
    const [open, setOpen] = React.useState(false);
    const headerStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 10,
        cursor: 'pointer',
        userSelect: 'none',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        color: 'var(--app-text-primary)'
    };
    const pillStyle = {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
        color: 'rgba(255, 225, 160, 0.9)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8
    };
    const contentWrap = {
        marginTop: 8,
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(0,0,0,0.05)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
        overflow: 'hidden'
    };
    const contentInner = {
        padding: 10,
        fontSize: 13,
        lineHeight: 1.6,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    };
    return (_jsxs("div", { style: { margin: '6px 0' }, children: [_jsxs("div", { style: headerStyle, onClick: () => setOpen(v => !v), children: [_jsxs("span", { style: pillStyle, children: [_jsx("span", { style: { display: 'inline-block', width: 6, height: 6, borderRadius: 6, background: 'rgba(255,225,160,0.95)', boxShadow: '0 0 10px rgba(255,225,160,0.9)' } }), title] }), _jsx(IconChevronDown, { size: 14, style: { transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 120ms ease', opacity: 0.85 } })] }), open && (_jsx("div", { style: contentWrap, children: _jsx("div", { style: contentInner, dangerouslySetInnerHTML: { __html: html } }) }))] }));
};
function splitThinkingBlocks(input) {
    const out = [];
    if (!input)
        return [{ type: 'text', content: '' }];
    const decodeAttr = (v) => {
        try {
            let s = String(v || '');
            s = s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
            s = s.replace(/&#10;/g, '\n').replace(/&#13;/g, '\r').replace(/&#9;/g, '\t');
            s = s.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t');
            return s;
        }
        catch {
            return v;
        }
    };
    const tagOpenRe = /<\s*(think|thinking)\b([^>]*?)(\/)?>/gi;
    let lastIndex = 0;
    let m;
    while ((m = tagOpenRe.exec(input)) !== null) {
        const name = (m[1] || '').toLowerCase();
        const attrs = m[2] || '';
        const selfClosing = !!m[3];
        const openStart = m.index;
        const openEnd = tagOpenRe.lastIndex;
        if (openStart > lastIndex) {
            out.push({ type: 'text', content: input.slice(lastIndex, openStart) });
        }
        // content attribute (single or double quotes)
        let attrContent = '';
        const attrMatch = attrs.match(/\bcontent\s*=\s*("([^"]*)"|'([^']*)')/i);
        if (attrMatch)
            attrContent = decodeAttr(attrMatch[2] ?? attrMatch[3] ?? '');
        if (selfClosing) {
            const thinkText = attrContent || '';
            out.push({ type: 'think', content: thinkText });
            lastIndex = openEnd;
            continue;
        }
        // find matching close tag
        const closeRe = new RegExp(`<\\s*\\/\\s*${name}\\s*>`, 'i');
        const closeMatch = closeRe.exec(input.slice(openEnd));
        if (closeMatch) {
            const closeIdx = openEnd + (closeMatch.index || 0);
            const inner = input.slice(openEnd, closeIdx);
            const thinkText = attrContent || inner;
            out.push({ type: 'think', content: thinkText });
            lastIndex = closeIdx + closeMatch[0].length + 0; // to absolute move we recompute below
            // Correct absolute lastIndex
            lastIndex = openEnd + closeMatch.index + closeMatch[0].length;
            // Move regex lastIndex as well if needed when overlapping (not strictly necessary)
            tagOpenRe.lastIndex = lastIndex;
        }
        else {
            // No closing tag found: treat as text from openStart
            out.push({ type: 'text', content: input.slice(openStart, openEnd) });
            lastIndex = openEnd;
        }
    }
    if (lastIndex < input.length)
        out.push({ type: 'text', content: input.slice(lastIndex) });
    if (out.length === 0)
        return [{ type: 'text', content: input }];
    return out;
}
// Safe markdown renderer for chat messages, supports streaming partial content
const MarkdownMessage = ({ content, className, onButtonClick, renderStyle = 'beautified', isStreaming = false, senderRole = 'assistant', timestamp, readStatus }) => {
    const t = useT();
    const looksLikeHtml = hasHtml(content);
    // Showdown: Markdown -> HTML
    const converter = React.useMemo(() => {
        try {
            showdown.setFlavor?.('github');
        }
        catch { }
        const c = new showdown.Converter({
            tables: true,
            strikethrough: true,
            simplifiedAutoLink: true,
            simpleLineBreaks: true,
            ghCodeBlocks: true,
            ghCompatibleHeaderId: true,
            openLinksInNewWindow: true,
            parseImgDimensions: true,
            literalMidWordUnderscores: true,
            requireSpaceBeforeHeadingText: true,
            underline: true,
            emoji: true,
            disableForced4SpacesIndentedSublists: true,
        });
        return c;
    }, []);
    const renderMarkdownOrTables = (text) => (_jsx("div", { style: { whiteSpace: 'normal' }, dangerouslySetInnerHTML: { __html: converter.makeHtml(String(text || '')) } }));
    // Showdown renderer helper (single definition)
    const renderStyledMarkdownBlock = (text) => {
        if (!text || !text.trim())
            return null;
        if (renderStyle === 'novel') {
            // Paper style with special handling for <think>/<thinking> blocks
            const isUser = senderRole === 'user';
            const segs = splitThinkingBlocks(text || '');
            return (_jsx("div", { className: className, style: { display: 'flex', justifyContent: isUser ? 'flex-end' : 'center', width: '100%' }, children: _jsx("div", { className: `novel-paper${isUser ? ' novel-user' : ''}`, children: _jsx("div", { className: "novel-content", children: segs.map((s, i) => s.type === 'think'
                            ? _jsx(ThinkCollapse, { html: converter.makeHtml(String(s.content || '')) }, `think-${i}`)
                            : _jsx(React.Fragment, { children: renderMarkdownOrTables(s.content) }, `txt-${i}`)) }) }) }));
        }
        if (renderStyle === 'messager') {
            const outgoing = senderRole === 'user';
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsxs("div", { className: `wa-bubble ${outgoing ? 'wa-outgoing' : 'wa-incoming'}`, children: [renderMarkdownOrTables(text), timestamp && (_jsxs("div", { className: "wa-meta", children: [_jsx("span", { className: "wa-time", children: new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }), outgoing && (_jsx("span", { className: `wa-ticks ${readStatus || 'sent'}`, children: "\u2713\u2713" }))] }))] }) }));
        }
        if (renderStyle === 'beautified') {
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: _jsx("div", { className: "assistant-block", children: _jsxs("div", { className: "assistant-box", children: [_jsx("svg", { className: "corner lt", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M9 1H1V9", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner rt", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M0 1H8V9", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner lb", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M9 8H1V0", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner rb", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M0 8H8V0", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("div", { className: "md-content", children: renderMarkdownOrTables(text) })] }) }) }));
        }
        if (renderStyle === 'framed') {
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: _jsx("div", { className: "assistant-framed", children: _jsxs("div", { className: "frame-box", children: [_jsx("svg", { className: "corner lt", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M9 1H1V9", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner rt", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M0 1H8V9", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner lb", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M9 8H1V0", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("svg", { className: "corner rb", width: "9", height: "9", viewBox: "0 0 9 9", fill: "none", children: _jsx("path", { d: "M0 8H8V0", stroke: "white", strokeOpacity: "0.8" }) }), _jsx("div", { className: "md-content", children: renderMarkdownOrTables(text) })] }) }) }));
        }
        // plain markdown
        return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: renderMarkdownOrTables(text, 'plain') }));
    };
    const handleHtmlClick = (e) => {
        const target = e.target;
        const btn = target?.closest('button');
        if (btn && onButtonClick) {
            const label = (btn.textContent || '').trim();
            if (label)
                onButtonClick(label);
        }
    };
    const hasHeavyMarkdown = /(```|^\s*#{1,6}\s+|\*\*|__|`[^`]|_[^_]|\|\s*\|)/m.test(content);
    // (moved inline fenced HTML handler below after helper definition)
    // Helper to render sanitized HTML optionally inside styled wrappers
    const renderSanitizedHtml = (rawHtml) => {
        // Sanitize and scope <style> to .mes_text like ST，直接注入而非 iframe
        const safe = sanitizeHtml(rawHtml);
        const scoped = (() => {
            try {
                const pfx = '.mes_text ';
                // Prefix all style rules to .mes_text container; map :root/html/body to container
                return safe.replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (_m, css) => {
                    const scopedCss = String(css || '').replace(/(^|})(\s*)([^@{}][^{]*)\{/g, (_m2, b, sp, sel) => {
                        const parts = String(sel || '').split(',');
                        const mapped = parts.map((s) => {
                            let t = s.trim();
                            if (!t)
                                return t;
                            if (t.startsWith(pfx))
                                return t;
                            if (/^:root\b/i.test(t))
                                return pfx.trim();
                            // Replace leading html/body
                            t = t.replace(/^html\b\s*/i, '').replace(/^body\b\s*/i, '').trim();
                            if (!t)
                                return pfx.trim();
                            return `${pfx}${t}`.replace(/\s+/g, ' ').trim();
                        });
                        return `${b}${sp}${mapped.join(', ')}{`;
                    });
                    // Add compact resets to avoid excessive gaps and pre-wrap issues
                    const resets = `${pfx}{white-space: normal;} ${pfx}details{margin:0;} ${pfx}summary{margin:0; list-style:none;} ${pfx}.info-card-summary{margin:0;} ${pfx}.info-content{margin:0;}`;
                    return `<style>${scopedCss}\n${resets}</style>`;
                });
            }
            catch {
                return safe;
            }
        })();
        return (_jsx(ScopedHtml, { html: scoped, className: "mes_text html-embed", style: { margin: '6px 0', padding: 0, background: 'transparent' }, onClick: handleHtmlClick }));
    };
    // Render raw full HTML page (<!doctype ...><html>...</html>) via SafeHtmlBlock so scripts/styles execute
    const renderRawHtmlPage = (raw) => {
        // Strip <html>/<head>/<body>, re-insert styles scoped to .mes_text
        const { body, styles } = (() => {
            try {
                const tpl = document.createElement('template');
                tpl.innerHTML = String(raw || '');
                const head = tpl.content.querySelector('head');
                const bodyEl = tpl.content.querySelector('body');
                let css = '';
                if (head)
                    for (const st of Array.from(head.querySelectorAll('style')))
                        css += (st.textContent || '') + '\n';
                if (bodyEl)
                    for (const st of Array.from(bodyEl.querySelectorAll('style')))
                        css += (st.textContent || '') + '\n';
                const inner = bodyEl ? bodyEl.innerHTML : tpl.innerHTML;
                return { body: inner || '', styles: css };
            }
            catch {
                return { body: raw, styles: '' };
            }
        })();
        const styleTag = styles ? `<style>${styles.replace(/(^|})(\s*)([^@{}][^{]*)\{/g, (_m, b, sp, sel) => {
            const parts = String(sel || '').split(',');
            const mapped = parts.map((s) => { const t = s.trim(); if (!t)
                return t; if (t.startsWith('.mes_text '))
                return t; return `.mes_text ${t}`; });
            return `${b}${sp}${mapped.join(', ')}{`;
        })}</style>` : '';
        return renderSanitizedHtml(`${styleTag}${body}`);
    };
    const isFullHtmlDoc = (s) => /<!doctype|<\s*html[\s>]/i.test(String(s || ''));
    // If message is a fenced HTML block starting with ```html, render its inner HTML
    const fenceMatch = content.match(/^```\s*html\s*\n([\s\S]*?)\n```\s*$/i);
    if (fenceMatch) {
        const inner = fenceMatch[1] || '';
        if (isFullHtmlDoc(inner))
            return renderRawHtmlPage(inner);
        const safe = sanitizeHtml(inner);
        if (safe)
            return renderSanitizedHtml(safe);
        const plain = inner.replace(/<[^>]*>/g, '');
        return renderStyledMarkdownBlock(plain);
    }
    // Handle inline fenced HTML blocks anywhere in the content (before generic HTML detection)
    {
        const fenceRe = /```\s*html[^\n]*\n([\s\S]*?)\n```/gi;
        let match;
        let last = 0;
        const parts = [];
        while ((match = fenceRe.exec(content))) {
            if (match.index > last)
                parts.push({ type: 'md', text: content.slice(last, match.index) });
            parts.push({ type: 'html', text: match[1] || '' });
            last = match.index + match[0].length;
        }
        if (last > 0) {
            if (last < content.length)
                parts.push({ type: 'md', text: content.slice(last) });
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: parts.map((p, idx) => {
                    if (p.type === 'html') {
                        const raw = p.text;
                        if (isFullHtmlDoc(raw))
                            return _jsx(React.Fragment, { children: renderRawHtmlPage(raw) }, `html-full-${idx}`);
                        const safe = sanitizeHtml(raw);
                        if (safe)
                            return _jsx(React.Fragment, { children: renderSanitizedHtml(safe) }, `html-${idx}`);
                        const plain = raw.replace(/<[^>]*>/g, '');
                        return _jsx(React.Fragment, { children: renderStyledMarkdownBlock(plain) }, `html-fallback-${idx}`);
                    }
                    return _jsx(React.Fragment, { children: renderStyledMarkdownBlock(p.text) }, `md-${idx}`);
                }) }));
        }
    }
    // Special handling: in novel style, collapse <think>/<thinking> even if HTML-like tags exist
    if (renderStyle === 'novel') {
        const isUser = senderRole === 'user';
        const segs = splitThinkingBlocks(content || '');
        // If we actually split into segments (i.e., think tags present) or we explicitly prefer novel rendering,
        // render within the novel paper container and use Markdown for text parts.
        if (segs.length > 1 || /<\s*(think|thinking)\b/i.test(content)) {
            return (_jsx("div", { className: className, style: { display: 'flex', justifyContent: isUser ? 'flex-end' : 'center', width: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsx("div", { className: `novel-paper${isUser ? ' novel-user' : ''}`, children: _jsx("div", { className: "novel-content", children: segs.map((s, i) => s.type === 'think'
                            ? _jsx(ThinkCollapse, { html: converter.makeHtml(String(s.content || '')) }, `think-inline-${i}`)
                            : _jsx(React.Fragment, { children: renderMarkdownOrTables(s.content) }, `txt-inline-${i}`)) }) }) }));
        }
    }
    if (looksLikeHtml) {
        // 支持“完整HTML页面 + 追加文本”的混合内容：提取完整页面片段并逐段渲染
        const raw = content;
        const docRe = /<!doctype[\s\S]*?<\s*html\b[\s\S]*?<\s*\/\s*html\s*>/i;
        const m = raw.match(docRe);
        if (m) {
            const start = m.index || 0;
            const end = start + m[0].length;
            const before = raw.slice(0, start).trim();
            const doc = m[0];
            const after = raw.slice(end).trim();
            return (_jsxs("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: [before && (_jsx("div", { style: { marginBottom: 6 }, children: renderMarkdownOrTables(before) })), renderRawHtmlPage(doc), after && (_jsx("div", { style: { marginTop: 6 }, children: renderMarkdownOrTables(after) }))] }));
        }
        if (raw && raw.trim())
            return renderSanitizedHtml(raw);
        const plain = raw.replace(/<[^>]*>/g, '');
        return renderStyledMarkdownBlock(plain);
    }
    // (removed duplicate inline fenced handler)
    // Styled rendering: support both plain text and markdown
    if (renderStyle === 'beautified' || renderStyle === 'framed' || renderStyle === 'novel') {
        // If heavy markdown exists, wrap the whole content in a styled container and render markdown inside
        if (hasHeavyMarkdown) {
            const startsWithCodeFence = /^\s*```(?!\s*html)/i.test(content);
            if (startsWithCodeFence) {
                // Special: code-fence-first -> white outline frame
                return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsx("div", { style: {
                            display: 'inline-block', maxWidth: '100%',
                            border: '1px solid rgba(255,255,255,0.9)', borderRadius: 8,
                            padding: '8px 10px'
                        }, children: renderMarkdownOrTables(content) }) }));
            }
            if (renderStyle === 'beautified') {
                return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: _jsx("div", { className: "assistant-block", children: _jsx("div", { className: "assistant-box", children: _jsx("div", { className: "md-content", children: renderMarkdownOrTables(content) }) }) }) }));
            }
            if (renderStyle === 'novel') {
                const isUser = senderRole === 'user';
                const segs = splitThinkingBlocks(content || '');
                return (_jsx("div", { className: className, style: { display: 'flex', justifyContent: isUser ? 'flex-end' : 'center', width: '100%' }, children: _jsx("div", { className: `novel-paper${isUser ? ' novel-user' : ''}`, children: _jsx("div", { className: "novel-content", children: segs.map((s, i) => s.type === 'think'
                                ? _jsx(ThinkCollapse, { html: converter.makeHtml(String(s.content || '')) }, `think-h-${i}`)
                                : _jsx(React.Fragment, { children: renderMarkdownOrTables(s.content) }, `txt-h-${i}`)) }) }) }));
            }
            // framed heavy markdown wrapper
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: _jsx("div", { className: "assistant-framed", children: _jsx("div", { className: "frame-box", children: _jsx("div", { className: "md-content", children: renderMarkdownOrTables(content) }) }) }) }));
        }
        // Plain text paragraphs retain the previous per-paragraph styling
        const paras = content.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
        if (renderStyle === 'messager') {
            const outgoing = senderRole === 'user';
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: paras.map((p, idx) => {
                    const last = idx === paras.length - 1;
                    return (_jsxs("div", { className: `wa-bubble ${outgoing ? 'wa-outgoing' : 'wa-incoming'} ${last ? 'wa-last' : ''}`, children: [renderMarkdownOrTables(p), last && timestamp && (_jsxs("div", { className: "wa-meta", children: [_jsx("span", { className: "wa-time", children: new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }), outgoing && (_jsx("span", { className: `wa-ticks ${readStatus || 'sent'}`, children: "\u2713\u2713" }))] }))] }, idx));
                }) }));
        }
        if (renderStyle === 'novel') {
            // Always use Markdown renderer with <think> collapsing
            const isUser = senderRole === 'user';
            const segs = splitThinkingBlocks(content || '');
            return (_jsx("div", { className: className, style: { display: 'flex', justifyContent: isUser ? 'flex-end' : 'center', width: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsx("div", { className: `novel-paper${isUser ? ' novel-user' : ''}`, children: _jsx("div", { className: "novel-content", children: segs.map((s, i) => s.type === 'think'
                            ? _jsx(ThinkCollapse, { html: converter.makeHtml(String(s.content || '')) }, `think-p-${i}`)
                            : _jsx(React.Fragment, { children: renderMarkdownOrTables(s.content) }, `txt-p-${i}`)) }) }) }));
        }
        // Default to markdown rendering even when no explicit code fences
        if (renderStyle === 'beautified') {
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsx("div", { className: "assistant-block", children: _jsx("div", { className: "assistant-box", children: _jsx("div", { className: "md-content", children: renderMarkdownOrTables(content, 'beauty2') }) }) }) }));
        }
        if (renderStyle === 'framed') {
            return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere' }, children: _jsx("div", { className: "assistant-framed", children: _jsx("div", { className: "frame-box", children: _jsx("div", { className: "md-content", children: renderMarkdownOrTables(content, 'framed2') }) }) }) }));
        }
        // Fallback: plain paragraphs (should rarely be used now)
        return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: paras.map((p, idx) => (_jsx("div", { dangerouslySetInnerHTML: { __html: converter.makeHtml(String(p || '')) } }, idx))) }));
    }
    return (_jsx("div", { className: className, style: { wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }, children: renderMarkdownOrTables(content) }));
};
export default React.memo(MarkdownMessage);
